import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LabelList,
} from 'recharts';
import { useToast, Box, Flex, Heading, Text, Button, ButtonGroup, Card, Grid, GridItem, VStack, HStack, Spinner } from 'components-lib';
import ReportService from '../../../services/reportService';
import ReportLoader from '../../../services/reportLoader';
// axiosInstance moved to components that need it; not required here
import reportMappers from '../../../services/reportMappers';
import { useGlobalFilters } from '../../../contexts/GlobalFiltersContext';
import { Filter } from 'lucide-react';

const ModernReports = () => {
  const [genderData, setGenderData] = useState([]);
  const [visitTrendData, setVisitTrendData] = useState([]);
  const [ageData, setAgeData] = useState([]);
  const [patientChartType, setPatientChartType] = useState('age');
  const [chartView, setChartView] = useState('appointments');
  const [followUpTab, setFollowUpTab] = useState('byPeriod');
  const [visitChartView, setVisitChartView] = useState('repetition');
  const [activeFilter, setActiveFilter] = useState('all');
  const [visitRepetitionData, setVisitRepetitionData] = useState([]);
  const [patientRetentionByPeriod, setPatientRetentionByPeriod] = useState([]);
  const [patientRetentionOverall, setPatientRetentionOverall] = useState([]);
  const [patientRetentionTotal, setPatientRetentionTotal] = useState(null);
  const [followUpSummary, setFollowUpSummary] = useState({
    compliance: [],
    byPeriod: [],
    chartData: [],
  });
  const [followUpTrendData, setFollowUpTrendData] = useState([]);
  const loadReportsTimeoutRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const lastSuccessfulReportsRef = useRef(null);
  const [summaryCards, setSummaryCards] = useState([]);
  const [billsData, setBillsData] = useState({
    revenue: { total: 0, byDoctor: [] },
    refunds: { total: 0, incomplete: 0, missed: 0, byDoctor: [] },
  });
  const [billsTab, setBillsTab] = useState('revenue');
  const [patientGrowthData, setPatientGrowthData] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState(null);
  const toast = useToast();

  // Chart styling constants (keep all bar charts visually consistent)
  const CHART_MARGIN = { top: 20, right: 40, left: 0, bottom: 20 };
  const CHART_BAR_SIZE = 40;
  const CHART_BAR_GAP = 10;
  const CHART_RADIUS = [8, 8, 0, 0];
  const AXIS_STROKE = '#6B7280';
  const GRID_STROKE = '#E5E7EB';
  const TICK_FONT_SIZE = 12;
  const STROKE_WIDTH = 1;

  const resetReportState = useCallback(() => {
    setVisitTrendData([]);
    setPatientGrowthData([]);
    setVisitRepetitionData([]);
    setPatientRetentionByPeriod([]);
    setPatientRetentionOverall([]);
    setPatientRetentionTotal(null);
    setFollowUpSummary({ compliance: [], byPeriod: [], chartData: [] });
    setFollowUpTrendData([]);
    setSummaryCards([]);
    setAgeData([]);
    setGenderData([]);
    setBillsData({ revenue: { total: 0, byDoctor: [] }, refunds: { total: 0, incomplete: 0, missed: 0, byDoctor: [] } });
    setBillsTab('revenue');
  }, []);

  const { filters, actions } = useGlobalFilters();
  const { clinicId: gfClinicId, doctorIdsEffective, fromDate: gfFromDate, toDate: gfToDate } = filters || {};
  const { setFromDate, setToDate } = actions;
  const doctorParam = Array.isArray(doctorIdsEffective) && doctorIdsEffective.length === 1 ? doctorIdsEffective[0] : undefined;

  const calculateDateRange = (filterType) => {
    const today = new Date();
    let fromDate, toDate;

    switch (filterType) {
      case 'all': {
        const twoYearsAgo = new Date(today);
        twoYearsAgo.setFullYear(today.getFullYear() - 2);
        fromDate = twoYearsAgo.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      }
      case 'weekly': {
        const currentDay = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
        fromDate = monday.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      }
      case 'monthly': {
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      }
      case 'quarterly': {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        fromDate = new Date(today.getFullYear(), quarterStartMonth, 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      }
      case 'yearly': {
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      }
      default: {
        fromDate = gfFromDate || today.toISOString().split('T')[0];
        toDate = gfToDate || today.toISOString().split('T')[0];
      }
    }

    return { fromDate, toDate };
  };

  const applyMappedReports = useCallback((mapped = {}, loaderErrors = null) => {
    setVisitTrendData(Array.isArray(mapped.visitTrendData) ? mapped.visitTrendData : []);
    setPatientGrowthData(Array.isArray(mapped.patientGrowthData) ? mapped.patientGrowthData : []);

    const visitRepetitionMapped = Array.isArray(mapped.visitRepetitionData)
      ? mapped.visitRepetitionData
      : [];
    setVisitRepetitionData(visitRepetitionMapped);

    const retention = mapped.patientRetention || {};
    const retentionByPeriod = Array.isArray(retention.retentionByPeriod) ? retention.retentionByPeriod : [];
    const overallRetention = Array.isArray(retention.overallRetention) ? retention.overallRetention : [];

    let totalPatients = null;
    const retentionOverallForChart = overallRetention.reduce((acc, entry, index) => {
      const label = entry.status || entry.patient_status || `Status ${index + 1}`;
      if (/total\s*patient?/i.test(String(label))) {
        totalPatients = entry.count ?? entry.patient_count ?? totalPatients;
        return acc;
      }
      acc.push(entry);
      return acc;
    }, []);

    setPatientRetentionByPeriod(retentionByPeriod);
    setPatientRetentionOverall(retentionOverallForChart);
    setPatientRetentionTotal(totalPatients);

    const followUpCompliance = Array.isArray(mapped.followUpCompliance) ? mapped.followUpCompliance : [];
    const followUpByPeriod = Array.isArray(mapped.followUpByPeriod) ? mapped.followUpByPeriod : [];

    const complianceLegend = [
      { test: /within\s*7/i, name: 'Within 7 Days', color: '#0D9488' },
      { test: /no\s*follow/i, name: 'No Follow-up', color: '#F97316' },
      { test: /late/i, name: 'Late Follow-up', color: '#3B82F6' },
    ];

    const derivedFollowUpChart = followUpCompliance
      .filter((entry) => !/total\s*prescription?/i.test(String(entry.follow_up_status || entry.status || '')))
      .map((entry) => {
        const rawStatus = entry.status || '';
        const legend = complianceLegend.find((l) => l.test.test(String(rawStatus))) || {};
        const value = entry.count ?? entry.patient_count ?? 0;
        return {
          name: rawStatus || '',
          value,
          patients: value,
          color: legend.color || '#A78BFA',
        };
      });

    setFollowUpSummary({
      compliance: followUpCompliance,
      byPeriod: followUpByPeriod,
      chartData: derivedFollowUpChart,
    });

    const totalEntry = followUpCompliance.find((entry) => /total\s*prescription?/i.test(String(entry.status)));
    const noFollowEntry = followUpCompliance.find((entry) => /no\s*follow/i.test(String(entry.status)));
    const totalScheduled = totalEntry?.count ?? followUpCompliance.reduce((sum, entry) => sum + (entry.count || 0), 0);
    const pendingCount = noFollowEntry?.count ?? 0;
    const fallbackCompleted = Math.max(totalScheduled - pendingCount, 0);

    const followUpTrendSource = followUpByPeriod.length
      ? followUpByPeriod
      : [{ range: 'Current Period', count: fallbackCompleted }];

    const normalizedFollowUpTrend = followUpTrendSource.map((entry, index) => {
      const label = entry.range || entry.time_period || `Period ${index + 1}`;
      const count = entry.count || entry.patient_count || 0;
      const isNoFollow = /no\s*follow/i.test(String(label));
      const completed = isNoFollow ? 0 : count;
      const pending = isNoFollow ? count : Math.max(totalScheduled - count, 0);
      return {
        period: label,
        completed,
        pending,
        scheduled: totalScheduled,
      };
    });

    setFollowUpTrendData(normalizedFollowUpTrend);
    setAgeData(Array.isArray(mapped.ageData) ? mapped.ageData : []);
    setGenderData(Array.isArray(mapped.genderData) ? mapped.genderData : []);

    const cards = Array.isArray(mapped.summaryCards) ? mapped.summaryCards.slice(0, 4) : [];
    setSummaryCards(cards);

    const bills = mapped.bills || { revenue: { total: 0, byDoctor: [] }, refunds: { total: 0, incomplete: 0, missed: 0, byDoctor: [] } };
    setBillsData(bills);

    if (loaderErrors && Object.keys(loaderErrors).length) {
      // Consider surfacing partial failures
    }
  }, []);

  const getDoctorIdFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && (parsed.id || parsed.userId) ? (parsed.id || parsed.userId) : null;
    } catch (e) {
      console.error('[Reports] Error reading user from localStorage', e);
      return null;
    }
  };

  const getUserFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      console.error('[Reports] Error reading user from localStorage', e);
      return null;
    }
  };

  const getTokenPayload = () => {
    try {
      const raw = localStorage.getItem('tokenPayload');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[Reports] Error reading tokenPayload from localStorage', e);
      return null;
    }
  };

  const tokenPayload = getTokenPayload();
  const clinicIdFromToken = tokenPayload?.clinic_id;

  const userFromLS = getUserFromLocalStorage();
  const roleCandidate = (userFromLS && (userFromLS.role || userFromLS.ric || userFromLS.role_in_clinic)) || (tokenPayload && (tokenPayload.role || tokenPayload.ric));
  const normalizedRole = roleCandidate ? String(roleCandidate).toLowerCase() : '';
  const isOwner = normalizedRole === 'owner' || normalizedRole === 'clinic_owner' || normalizedRole === 'owner_in_clinic';
  const isAdmin = normalizedRole === 'admin' || normalizedRole === 'superadmin' || normalizedRole === 'administrator';
  const isDoctor = userFromLS && (String(userFromLS.role || '').toLowerCase() === 'doctor');
  const useClinicLevelStats = !!(clinicIdFromToken && !isAdmin);

  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (loadReportsTimeoutRef.current) {
      clearTimeout(loadReportsTimeoutRef.current);
      loadReportsTimeoutRef.current = null;
    }

    loadReportsTimeoutRef.current = setTimeout(() => {
      const requestId = Date.now();
      latestRequestIdRef.current = requestId;

      const loadReports = async () => {
        if (!lastSuccessfulReportsRef.current) {
          resetReportState();
        }
        setLoadingReports(true);
        setReportsError(null);

        try {
          const fallbackDoctor = getDoctorIdFromLocalStorage();
          const startDate = gfFromDate || undefined;
          const endDate = gfToDate || undefined;
          const clinic_id = gfClinicId != null ? gfClinicId : (clinicIdFromToken || undefined);
          const doctor_id = doctorParam != null ? doctorParam : (fallbackDoctor || undefined);

          const params = { startDate, endDate, clinic_id, doctor_id };

          const loaderResult = await ReportLoader.loadAndMap(params);
          if (cancelled || latestRequestIdRef.current !== requestId) return;

          const { mapped = {}, errors: loaderErrors = null } = loaderResult || {};

          applyMappedReports(mapped, loaderErrors);
          lastSuccessfulReportsRef.current = { mapped, loaderErrors };
        } catch (error) {
          console.error('[Reports] Error loading reports', error);
          if (cancelled || latestRequestIdRef.current !== requestId) return;
          const message = error?.message || 'Unable to load reports';
          const fallback = lastSuccessfulReportsRef.current;

          if (fallback?.mapped) {
            applyMappedReports(fallback.mapped, fallback.loaderErrors);
            setReportsError(null);
          } else {
            setReportsError(message);
            toast({
              title: 'Unable to load reports',
              description: message,
              status: 'error',
              duration: 6000,
              isClosable: true,
            });
          }
        } finally {
          if (cancelled || latestRequestIdRef.current !== requestId) return;
          setLoadingReports(false);
        }
      };

      loadReports();
    }, 450);

    return () => {
      cancelled = true;
      if (loadReportsTimeoutRef.current) {
        clearTimeout(loadReportsTimeoutRef.current);
        loadReportsTimeoutRef.current = null;
      }
    };
  }, [
    gfClinicId,
    doctorIdsEffective,
    gfFromDate,
    gfToDate,
    clinicIdFromToken,
    doctorParam,
    resetReportState,
    toast,
    applyMappedReports,
  ]);

  const [loadingPatientDistribution, setLoadingPatientDistribution] = useState(false);
  const [patientDistributionError, setPatientDistributionError] = useState(null);

  // Fetch clinics/doctors are handled inside ClinicDoctorSelector for admin/owner UI.

  useEffect(() => {
    // Default selection logic:
    // - Admins: no default (admin uses the ClinicDoctorSelector)
    // - Doctors: prefer doctor id from localStorage and do NOT use clinic-level stats
    // - Owners: if they also have a doctor id prefer doctor-level; otherwise default to clinic id from token
    // - Others: fallback to clinic id if present, otherwise doctor id
    if (isAdmin) return;

    const dId = getDoctorIdFromLocalStorage();

    if (isDoctor && dId) {
      setSelectedDoctorId(String(dId));
      setSelectedClinicId(null);
      return;
    }

    if (isOwner) {
      // Owner: prefer doctor id (if owner is also a doctor), else use clinic id
      if (dId) {
        setSelectedDoctorId(String(dId));
        return;
      }
      if (clinicIdFromToken) {
        setSelectedClinicId(clinicIdFromToken);
        return;
      }
    }

    if (clinicIdFromToken) {
      setSelectedClinicId(clinicIdFromToken);
      return;
    }

    if (dId) {
      setSelectedDoctorId(String(dId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, clinicIdFromToken, isDoctor, isOwner]);

  useEffect(() => {
    if (!isAdmin || !selectedClinicId) return;
    let mounted = true;
    const loadClinicDistribution = async () => {
      setLoadingPatientDistribution(true);
      setPatientDistributionError(null);
      try {
        const resp = await ReportService.getPatientDistributionByClinic(String(selectedClinicId));
        const mapped = reportMappers.mapPatientDistribution(resp);
        if (!mounted) return;
        if (mapped && mapped.ageData && mapped.ageData.length) setAgeData(mapped.ageData);
        if (mapped && mapped.genderData && mapped.genderData.length) setGenderData(mapped.genderData);
      } catch (e) {
        console.error('[Reports] Error fetching patient distribution for admin-selected clinic', selectedClinicId, e);
        // Handle "no data found" errors gracefully - don't set error state for empty data
        if (e.category !== 'not_found' && !String(e.message).toLowerCase().includes('no patient distribution')) {
          setPatientDistributionError(e);
        } else {
          // For "no data found" cases, just set empty data
          setAgeData([]);
          setGenderData([]);
        }
      } finally {
        if (mounted) setLoadingPatientDistribution(false);
      }
    };

    loadClinicDistribution();
    return () => { mounted = false; };
  }, [isAdmin, selectedClinicId]);

  // Fetch patient distribution when selectedDoctorId changes
  useEffect(() => {
    if (!selectedDoctorId) return;
    let mounted = true;
    const loadPatientDistribution = async () => {
      setLoadingPatientDistribution(true);
      setPatientDistributionError(null);
      try {
        const resp = await ReportService.getPatientDistribution(selectedDoctorId || '');
        const mapped = reportMappers.mapPatientDistribution(resp);
        if (!mounted) return;
        if (mapped && mapped.ageData && mapped.ageData.length) setAgeData(mapped.ageData);
        if (mapped && mapped.genderData && mapped.genderData.length) setGenderData(mapped.genderData);
      } catch (e) {
        console.error('[Reports] Error fetching patient distribution for doctor', selectedDoctorId, e);
        if (e.category !== 'not_found' && !String(e.message).toLowerCase().includes('no patient distribution')) {
          setPatientDistributionError(e);
        } else {
          // For "no data found" cases, just set empty data
          setAgeData([]);
          setGenderData([]);
        }
      } finally {
        if (mounted) setLoadingPatientDistribution(false);
      }
    };

    loadPatientDistribution();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctorId]);

  useEffect(() => {
    if (!gfClinicId && !(doctorIdsEffective && doctorIdsEffective.length)) return;
    let mounted = true;
    const load = async () => {
      setLoadingPatientDistribution(true);
      setPatientDistributionError(null);
      try {
        const doctor_id = Array.isArray(doctorIdsEffective) && doctorIdsEffective.length === 1 ? doctorIdsEffective[0] : undefined;
        const params = { startDate: gfFromDate, endDate: gfToDate };
        if (doctor_id) params.doctor_id = doctor_id;
        if (gfClinicId != null) params.clinic_id = gfClinicId;

        const resp = await ReportService.getPatientDistribution(params);
        const mapped = reportMappers.mapPatientDistribution(resp);
        if (!mounted) return;
        if (mapped && mapped.ageData && mapped.ageData.length) setAgeData(mapped.ageData);
        if (mapped && mapped.genderData && mapped.genderData.length) setGenderData(mapped.genderData);
      } catch (e) {
        console.error('[Reports] Error fetching patient distribution for global filters', { gfClinicId, doctorIdsEffective }, e);
        if (e.category !== 'not_found' && !String(e.message).toLowerCase().includes('no patient distribution')) {
          setPatientDistributionError(e);
        } else {
          // For "no data found" cases, just set empty data
          setAgeData([]);
          setGenderData([]);
        }
      } finally {
        if (mounted) setLoadingPatientDistribution(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [gfClinicId, doctorIdsEffective, gfFromDate, gfToDate]);

  // If using clinic-level stats (non-admin users with clinic id), fetch distribution by clinic
  useEffect(() => {
    if (!useClinicLevelStats || !clinicIdFromToken) return;
    let mounted = true;
    const loadClinicDistribution = async () => {
      setLoadingPatientDistribution(true);
      setPatientDistributionError(null);
      try {
        // Attempt to fetch distribution by passing clinic id to the same service.
        // Backend is expected to use token or clinic param when doctor id is absent.
        const resp = await ReportService.getPatientDistributionByClinic(String(clinicIdFromToken));
        const mapped = reportMappers.mapPatientDistribution(resp);
        if (!mounted) return;
        if (mapped && mapped.ageData && mapped.ageData.length) setAgeData(mapped.ageData);
        if (mapped && mapped.genderData && mapped.genderData.length) setGenderData(mapped.genderData);
      } catch (e) {
        console.error('[Reports] Error fetching patient distribution for clinic', clinicIdFromToken, e);
        // Handle "no data found" errors gracefully - don't set error state for empty data
        if (e.category !== 'not_found' && !String(e.message).toLowerCase().includes('no patient distribution')) {
          setPatientDistributionError(e);
        } else {
          setAgeData([]);
          setGenderData([]);
        }
      } finally {
        if (mounted) setLoadingPatientDistribution(false);
      }
    };

    loadClinicDistribution();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useClinicLevelStats, clinicIdFromToken]);

  // Normalize age data that may come as { age, count, percentage }
  // to the UI shape { age, chronic, acute, percentage }
  const normalizedAgeData = (ageData || []).map((it) => {
    // If API provides `count` use it as total and split proportionally if possible.
    // For now, prefer to put all into 'acute' to show a single bar when chronic/acute not provided.
    if (it.chronic != null || it.acute != null) return it;
    const total = (it.count != null) ? it.count : (it.total || 0);
    return {
      age: it.age || it.label || 'Unknown',
      chronic: 0,
      acute: total,
      percentage: it.percentage != null ? it.percentage : null,
    };
  });

  const hasPatientDistData = () => {
    if (patientChartType === 'age') return Array.isArray(normalizedAgeData) && normalizedAgeData.length > 0;
    return Array.isArray(genderData) && genderData.length > 0;
  };

  const hasAppointmentsData = () => {
    if (chartView === 'appointments') return Array.isArray(visitTrendData) && visitTrendData.length > 0;
    if (followUpTab === 'byPeriod') return Array.isArray(followUpTrendData) && followUpTrendData.length > 0;
    if (followUpTab === 'compliance') return Array.isArray(followUpSummary.chartData) && followUpSummary.chartData.length > 0;
    return false;
  };

  const hasVisitRepetitionData = () => {
    if (visitChartView === 'repetition') return Array.isArray(visitRepetitionData) && visitRepetitionData.length > 0;
    return (Array.isArray(patientRetentionOverall) && patientRetentionOverall.length > 0) ||
      (Array.isArray(patientRetentionByPeriod) && patientRetentionByPeriod.length > 0);
  };

  const hasPatientGrowthData = () => Array.isArray(patientGrowthData) && patientGrowthData.length > 0;

  const hasBillsData = () => {
    if (billsTab === 'revenue') return Array.isArray(billsData?.revenue?.byPeriod) && billsData.revenue.byPeriod.length > 0;
    return Array.isArray(billsData?.refunds?.byPeriod) && billsData.refunds.byPeriod.length > 0;
  };

  return (
    <Box minH="100vh" w="full" mt={4} px={8}>
      {/* Header */}
      <Card bg="white" mb={4} borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6}>
        <Flex justify="space-between" align="center">
          <Heading as="h1" size="2xl">Analytics Dashboard</Heading>
          <Flex direction={{ base: "column", lg: "row" }} align="center" justify="space-between" gap={4}>
            <Flex align="center" gap={4}>
              <Flex align="center" gap={2}>
                <Filter size={20} />
                <Text fontWeight="medium">Filters</Text>
              </Flex>
              <Flex gap={2}>
                {["all", "weekly", "monthly", "quarterly", "yearly"].map((filter) => (
                  <Button
                    key={filter}
                    onClick={() => {
                      setActiveFilter(filter);
                      if (setFromDate && setToDate) {
                        const { fromDate, toDate } = calculateDateRange(filter);
                        setFromDate(fromDate);
                        setToDate(toDate);
                      }
                    }}
                    px={4}
                    py={2}
                    borderRadius="xl"
                    fontWeight="medium"
                    bg={activeFilter === filter ? "blue.500" : "white"}
                    color={activeFilter === filter ? "white" : "gray.700"}
                    boxShadow={activeFilter === filter ? "lg" : "none"}
                    border="1px solid"
                    borderColor={activeFilter === filter ? "transparent" : "gray.200"}
                    _hover={activeFilter === filter ? {} : { bg: "gray.200", color: "gray.900" }}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </Button>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      {/* Main Charts Grid */}
      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={8} mb={8}>
        {/* Patient Distribution Card */}
        <Card bg="white" borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6} h="full">
          <Flex justify="space-between" align="center" mb={2}>
            <Heading as="h2" size="md">Patient Distribution</Heading>
            <Flex align="center">
              <ButtonGroup isAttached bg="gray.100" borderRadius="xl" p={1}>
                <Button
                  onClick={() => setPatientChartType("age")}
                  bg={patientChartType === "age" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={patientChartType === "age" ? "gray.200" : "transparent"}
                  color={patientChartType === "age" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Age
                </Button>
                <Button
                  onClick={() => setPatientChartType("gender")}
                  bg={patientChartType === "gender" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={patientChartType === "gender" ? "gray.200" : "transparent"}
                  color={patientChartType === "gender" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Gender
                </Button>
              </ButtonGroup>
            </Flex>
          </Flex>

          <Box h="320px">
            {loadingPatientDistribution ? (
              <Flex align="center" justify="center" h="full" color="gray.500">Loading patient distribution...</Flex>
            ) : patientDistributionError ? (
              <Flex align="center" justify="center" h="full" color="red.500">Failed to load distribution</Flex>
            ) : !hasPatientDistData() ? (
              <Flex direction="column" align="center" justify="center" h="full" color="gray.500" gap={2}>
                <Text fontWeight="medium">No data available</Text>
                <Text fontSize="sm">Please choose another filter</Text>
              </Flex>
            ) : (
              <ResponsiveContainer width="100%" height="100%" borderRadius='2xl'>
                {patientChartType === "age" ? (
                  <BarChart data={normalizedAgeData} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP} margin={CHART_MARGIN}>
                    <defs>
                      <linearGradient id="colorChronic" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.2} />
                      </linearGradient>
                      <linearGradient id="colorAcute" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EC4899" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#EC4899" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                    <XAxis
                      dataKey="age"
                      stroke={AXIS_STROKE}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }}
                    />
                    <YAxis
                      stroke={AXIS_STROKE}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const p = payload[0].payload || {};
                          const chronic = p.chronic != null ? p.chronic : 0;
                          const acute = p.acute != null ? p.acute : 0;
                          const total = chronic + acute;
                          return (
                            <Box bg="gray.800" p={3} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                              <Box fontWeight="semibold" color="gray.100">{label}</Box>
                              <Box color="purple.100">Chronic: {chronic}</Box>
                              <Box color="pink.100">Acute: {acute}</Box>
                              <Box color="gray.100">Total: {total}</Box>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="chronic" stackId="a" fill="url(#colorChronic)" stroke="#8B5CF6" strokeWidth={STROKE_WIDTH} radius={CHART_RADIUS} />
                    <Bar dataKey="acute" stackId="a" fill="url(#colorAcute)" stroke="#EC4899" strokeWidth={STROKE_WIDTH} radius={CHART_RADIUS} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <defs>
                      {genderData.map((entry, index) => (
                        <linearGradient key={`gradient-${index}`} id={`colorGender${index}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="5%" stopColor={entry.color} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={entry.color} stopOpacity={0.2} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={genderData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={55}
                      paddingAngle={3}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => {
                        const total = genderData.reduce((sum, item) => sum + item.value, 0);
                        const percent = ((value / total) * 100).toFixed(1);
                        return `${name}: ${percent}%`;
                      }}
                    >
                      {genderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`url(#colorGender${index})`} stroke={entry.color} strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const total = genderData.reduce((sum, item) => sum + item.value, 0);
                          const percent = ((payload[0].payload.value / total) * 100).toFixed(1);
                          return (
                            <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                              <Text fontWeight="semibold" color="gray.100">{payload[0].name}</Text>
                              <Text color="gray.100">{payload[0].value} patients ({percent}%)</Text>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                )}
              </ResponsiveContainer>
            )}
          </Box>
        </Card>
        {/* Appointments Types / Follow-up Analysis */}
        <Card bg="white" borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6} w="full" mb={8}>
          <Flex justify="space-between" align="center" mb={2}>
            <Heading as="h2" size="md">Appointments</Heading>
            <Flex align="center">
              <ButtonGroup isAttached bg="gray.100" borderRadius="xl" p={1}>
                <Button
                  onClick={() => setChartView("appointments")}
                  bg={chartView === "appointments" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={chartView === "appointments" ? "gray.200" : "transparent"}
                  color={chartView === "appointments" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Appointments
                </Button>
                <Button
                  onClick={() => setChartView("followup")}
                  bg={chartView === "followup" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={chartView === "followup" ? "gray.200" : "transparent"}
                  color={chartView === "followup" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Follow-up
                </Button>
              </ButtonGroup>
            </Flex>
          </Flex>

          <Flex direction="row" justify="flex-end" mb={4} gap={4} flexWrap="wrap">
            {chartView === "appointments" ? (
              <Flex align="center" gap={4} fontSize="xs">
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={2} borderRadius="full" bg="blue.700"></Box>
                  <Text fontSize="xs" color="gray.700">In-Clinic (Referred/Non-Referred)</Text>
                </Flex>
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={2} borderRadius="full" bg="purple.700"></Box>
                  <Text fontSize="xs" color="gray.700">Teleconsultations (Referred/Non-Referred)</Text>
                </Flex>
              </Flex>
            ) : (
              <Flex align="center" gap={4} fontSize="xs" flexWrap="wrap">
                {/* <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="green.600"></Box>
                  <Text fontSize="xs" color="gray.700">Expected</Text>
                </Flex>
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="teal.600"></Box>
                  <Text fontSize="xs" color="gray.700">Actual</Text>
                </Flex> */}
                <Box ml={4}>
                  <ButtonGroup isAttached bg="gray.100" borderRadius="xl" p={1} size="xs">
                    <Button
                      onClick={() => setFollowUpTab('byPeriod')}
                      bg={followUpTab === 'byPeriod' ? 'white' : 'transparent'}
                      border="1px solid"
                      borderColor={followUpTab === 'byPeriod' ? 'gray.200' : 'transparent'}
                      color={followUpTab === 'byPeriod' ? 'blue.600' : 'gray.600'}
                      fontSize="xs"
                      fontWeight="medium"
                    >
                      By period
                    </Button>
                    <Button
                      onClick={() => setFollowUpTab('compliance')}
                      bg={followUpTab === 'compliance' ? 'white' : 'transparent'}
                      border="1px solid"
                      borderColor={followUpTab === 'compliance' ? 'gray.200' : 'transparent'}
                      color={followUpTab === 'compliance' ? 'blue.600' : 'gray.600'}
                      fontSize="xs"
                      fontWeight="medium"
                    >
                      Compliance
                    </Button>
                  </ButtonGroup>
                </Box>
              </Flex>
            )}
          </Flex>

          <Box h="320px" position="relative">
            <ResponsiveContainer width="100%" height="100%">
              {!hasAppointmentsData() ? (
                <Flex direction="column" align="center" justify="center" h="full" color="gray.500" gap={2}>
                  <Text fontWeight="medium">No data available</Text>
                  <Text fontSize="sm">Please choose another filter</Text>
                </Flex>
              ) : chartView === "appointments" ? (
                <BarChart data={visitTrendData} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="colorInPersonReferred" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1D4ED8" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#1D4ED8" stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="colorInPersonNonReferred" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#60A5FA" stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="colorTeleReferred" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6D28D9" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#6D28D9" stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="colorTeleNonReferred" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#A78BFA" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="month" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = visitTrendData.find(d => d.month === label) || {};
                        return (
                          <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                            <Box fontWeight="semibold" color="gray.100">{label}</Box>
                            <Box color="blue.200">In-Clinic ({data.inPersonChange || '-'})</Box>
                            <Box color="blue.500">Referred: {data.inPerson?.referred ?? '-'}</Box>
                            <Box color="blue.300">Non-Referred: {data.inPerson?.nonReferred ?? '-'}</Box>
                            <Box mt={1} color="purple.200">Teleconsults ({data.teleconsultsChange || '-'})</Box>
                            <Box color="purple.100">Referred: {data.teleconsults?.referred ?? '-'}</Box>
                            <Box color="purple.300">Non-Referred: {data.teleconsults?.nonReferred ?? '-'}</Box>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="inPerson.referred" stackId="inPerson" fill="url(#colorInPersonReferred)" radius={[8, 0, 0, 0]} stroke="#1D4ED8" strokeWidth={STROKE_WIDTH} />
                  <Bar dataKey="inPerson.nonReferred" stackId="inPerson" fill="url(#colorInPersonNonReferred)" radius={[0, 8, 8, 0]} stroke="#60A5FA" strokeWidth={STROKE_WIDTH} />
                  <Bar dataKey="teleconsults.referred" stackId="teleconsults" fill="url(#colorTeleReferred)" radius={[8, 0, 0, 0]} stroke="#6D28D9" strokeWidth={STROKE_WIDTH} />
                  <Bar dataKey="teleconsults.nonReferred" stackId="teleconsults" fill="url(#colorTeleNonReferred)" radius={[0, 8, 8, 0]} stroke="#A78BFA" strokeWidth={STROKE_WIDTH} />
                </BarChart>
              ) : followUpTab === 'byPeriod' ? (
                <AreaChart data={followUpTrendData} margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                  <defs>
                    <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D9488" stopOpacity={0.85} />
                      <stop offset="95%" stopColor="#0D9488" stopOpacity={0.25} />
                    </linearGradient>
                    <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.85} />
                      <stop offset="95%" stopColor="#F97316" stopOpacity={0.25} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="period" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} allowDecimals={false} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const completed = payload.find((item) => item.dataKey === 'completed')?.value ?? 0;
                        const pending = payload.find((item) => item.dataKey === 'pending')?.value ?? 0;
                        return (
                          <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                            <Box fontWeight="semibold" color="gray.100">{label}</Box>
                            <Box color="teal.100">Completed: {completed}</Box>
                            <Box color="orange.100">Pending: {pending}</Box>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area type="monotone" dataKey="completed" stroke="#0D9488" fillOpacity={1} fill="url(#colorCompleted)" strokeWidth={3} />
                  <Area type="monotone" dataKey="pending" stroke="#F97316" fillOpacity={0.9} fill="url(#colorPending)" strokeWidth={3} />
                </AreaChart>
              ) : (
                <Flex align="center" justify="center" h="full">
                  <Box w="180px" h="160px">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={followUpSummary.chartData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={70} paddingAngle={4}>
                          {followUpSummary.chartData.map((entry, index) => {
                            const colors = ['#0D9488', '#F97316', '#3B82F6', '#EC4899', '#A78BFA'];
                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                          })}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                                  <Text fontWeight="semibold" color="gray.100">{payload[0].payload.name}</Text>
                                  <Text color="blue.100">Patients: {payload[0].payload.patients}</Text>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Flex>
              )}
            </ResponsiveContainer>
          </Box>
        </Card>
      </Grid>

      {/* Prescription Analytics */}
      {/* <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 w-full">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Prescription Analytics
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Distribution by pharmaceutical companies
              </p>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                // data={companyData}
                layout="vertical"
                barSize={30}
                margin={{ top: 20, right: 40, left: 0, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="colorPrescription" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.2} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis
                  type="number"
                  stroke={AXIS_STROKE}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }}
                  label={{
                    value: 'Number of Prescriptions',
                    position: 'bottom',
                    style: { fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="company"
                  stroke={AXIS_STROKE}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {payload[0].payload.company}
                          </p>
                          <p className="text-sm" style={{ color: '#e5e7eb' }}>
                            {payload[0].value} prescriptions
                          </p>
                          <p className="text-sm text-gray-500">
                            Growth: {payload[0].payload.growth}%
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="prescriptions"
                  fill="url(#colorPrescription)"
                  radius={[0, 8, 8, 0]}
                  stroke="#10B981"
                  strokeWidth={1}
                  name="Prescriptions"
                >
                  <LabelList
                    dataKey="growth"
                    position="right"
                    content={({ x, y, width, height, value, index }) => {
                      const entry = companyData[index];
                      const isPositive = entry.growth >= 0;
                      return (
                        <text
                          x={x + width + 10}
                          y={y + height / 2 + 5}
                          fill={isPositive ? "#10B981" : "#EF4444"}
                          fontSize={14}
                          fontWeight="bold"
                          textAnchor="start"
                        >
                          {isPositive ? "↑" : "↓"} {Math.abs(entry.growth)}%
                        </text>
                      );
                    }}
                  />
                </Bar>
                <ReferenceLine x={0} stroke={GRID_STROKE} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      {/* Visit Repetition & Patient Growth */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8} mb={8} w="full">
        {/* Visit Repetition */}
        <Card bg="white" borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6} w="full">
          <Flex justify="space-between" align="center" mb={2}>
            <Heading as="h2" size="md">Visit Repetition</Heading>
            <Flex align="center">
              <ButtonGroup isAttached bg="gray.100" borderRadius="xl" p={1}>
                <Button
                  onClick={() => setVisitChartView("repetition")}
                  bg={visitChartView === "repetition" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={visitChartView === "repetition" ? "gray.200" : "transparent"}
                  color={visitChartView === "repetition" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                  _hover={{ color: visitChartView === "repetition" ? "blue.600" : "gray.900" }}
                >
                  Visit Repetition
                </Button>
                <Button
                  onClick={() => setVisitChartView("return")}
                  bg={visitChartView === "return" ? "white" : "transparent"}
                  border="1px solid"
                  borderColor={visitChartView === "return" ? "gray.200" : "transparent"}
                  color={visitChartView === "return" ? "blue.600" : "gray.600"}
                  fontSize="xs"
                  fontWeight="medium"
                  _hover={{ color: visitChartView === "return" ? "blue.600" : "gray.900" }}
                >
                  Patient Return Rate
                </Button>
              </ButtonGroup>
            </Flex>
          </Flex>

          <Flex direction="row" justify="flex-end" mb={4} flexWrap="wrap">
            {visitChartView === "repetition" ? (
              <Flex align="center" gap={2} fontSize="xs">
                <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="purple.500"></Box>
                <Text fontSize="xs" color="gray.700">Number of Patients</Text>
              </Flex>
            ) : (
              <Flex align="center" gap={4} fontSize="xs" flexWrap="wrap">
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="blue.500"></Box>
                  <Text fontSize="xs" color="gray.700">0-10 days</Text>
                </Flex>
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="green.500"></Box>
                  <Text fontSize="xs" color="gray.700">10-20 days</Text>
                </Flex>
                <Flex align="center" gap={2}>
                  <Box as="span" display="inline-block" w={4} h={4} borderRadius="full" bg="yellow.500"></Box>
                  <Text fontSize="xs" color="gray.700">20-30 days</Text>
                </Flex>
              </Flex>
            )}
          </Flex>

          <Box h="320px" position="relative">
            <ResponsiveContainer width="100%" height="100%">
              {!hasVisitRepetitionData() ? (
                <Flex direction="column" align="center" justify="center" h="full" color="gray.500" gap={2}>
                  <Text fontWeight="medium">No data available</Text>
                  <Text fontSize="sm">Please choose another filter</Text>
                </Flex>
              ) : visitChartView === "repetition" ? (
                <BarChart data={visitRepetitionData} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="colorVisit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="visits" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box bg="gray.800" p={3} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                            <Text fontWeight="semibold" color="gray.100">{payload[0].payload.visits}</Text>
                            <Text color="gray.100">{payload[0].value} patients</Text>
                            <Text color="gray.100">{payload[0].payload.percentage}% of total</Text>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" fill="url(#colorVisit)" radius={CHART_RADIUS} stroke="#8B5CF6" strokeWidth={STROKE_WIDTH} />
                </BarChart>
              ) : (
                <Flex align="center" justify="center" h="full" color="gray.500">
                  {(patientRetentionOverall && patientRetentionOverall.length) || (patientRetentionByPeriod && patientRetentionByPeriod.length) ? (
                    <Flex direction={{ base: "column", "2xl": "row" }} gap={6} w="full" h="full" align="center" justify="center">
                      {patientRetentionOverall && patientRetentionOverall.length ? (
                        <Box flex={1} h="full" display={{ base: "none", "2xl": "block" }}>
                          <Heading as="h3" size="xs" color="gray.700" mb={2}>Return Status</Heading>
                          <ResponsiveContainer width="100%" height="80%">
                            <PieChart>
                              <defs>
                                {patientRetentionOverall.map((entry, idx) => (
                                  <linearGradient key={`ov-${idx}`} id={`colorOverall${idx}`} x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="5%" stopColor={entry.color || '#888'} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={entry.color || '#888'} stopOpacity={0.2} />
                                  </linearGradient>
                                ))}
                              </defs>
                              <Pie data={patientRetentionOverall} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3} dataKey="count" nameKey="range">
                                {patientRetentionOverall.map((entry, index) => (
                                  <Cell key={`cell-overall-${index}`} fill={`url(#colorOverall${index})`} stroke={entry.color} strokeWidth={2} />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                                        <Text color="blue.100">Patients: {payload[0].payload.count}</Text>
                                        <Text fontWeight="semibold" color="gray.100">{payload[0].payload.percentage}%</Text>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : null}
                      {patientRetentionByPeriod && patientRetentionByPeriod.length ? (
                        <Box flex={1} h="full">
                          <Heading as="h3" size="xs" color="gray.700" mb={2}>Retention By Period</Heading>
                          <ResponsiveContainer width="100%" height="80%">
                            <PieChart>
                              <defs>
                                {patientRetentionByPeriod.map((entry, idx) => (
                                  <linearGradient key={`per-${idx}`} id={`colorPeriod${idx}`} x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="5%" stopColor={entry.color || '#888'} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={entry.color || '#888'} stopOpacity={0.2} />
                                  </linearGradient>
                                ))}
                              </defs>
                              <Pie data={patientRetentionByPeriod} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3} dataKey="count" nameKey="range">
                                {patientRetentionByPeriod.map((entry, index) => (
                                  <Cell key={`cell-period-${index}`} fill={`url(#colorPeriod${index})`} stroke={entry.color} strokeWidth={2} />
                                ))}
                              </Pie>
                              {/* <Tooltip formatter={(value, name, props) => [`${props.payload.percentage}%`, name ]} /> */}
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <Box bg="gray.800" p={2} rounded="md" shadow="sm" border="1px solid" borderColor="gray.900" fontSize="xs">
                                        <Text color="blue.100"> {payload[0].name}</Text>
                                        <Text fontWeight="semibold" color="gray.100">{payload[0].payload.percentage}%</Text>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : null}
                    </Flex>
                  ) : null}
                </Flex>
              )}
            </ResponsiveContainer>
          </Box>
        </Card>

        {/* Patient Growth Trend */}
        <Card bg="white" borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6} w="full">
          <Box mb={6}>
            <Heading as="h2" size="md">Patient Growth Trend</Heading>
            <Text fontSize="sm" color="gray.600">Monthly growth in total patient count</Text>
          </Box>

          <Box h="320px" position="relative">
            <ResponsiveContainer width="100%" height="100%">
              {!hasPatientGrowthData() ? (
                <Flex direction="column" align="center" justify="center" h="full" color="gray.500" gap={2}>
                  <Text fontWeight="medium">No data available</Text>
                  <Text fontSize="sm">Please choose another filter</Text>
                </Flex>
              ) : (
                <BarChart data={patientGrowthData} margin={CHART_MARGIN} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP}>
                  <defs>
                    <linearGradient id="colorGrowthBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="month" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE, fontSize: TICK_FONT_SIZE }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box bg="gray.800" p={3} rounded="md" shadow="sm" border="1px solid" borderColor="gray.800" fontSize="xs">
                            <Text fontWeight="semibold" color="gray.100">{label}</Text>
                            <Text color="blue.100">Patients: {payload[0].payload.patients}</Text>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="patients" fill="url(#colorGrowthBar)" radius={CHART_RADIUS} stroke="#3B82F6" strokeWidth={STROKE_WIDTH} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        </Card>
      </Grid>

      <Card bg="white" borderRadius='2xl' borderWidth='1px' borderStyle='solid' borderColor='gray.200' p={6} w="full" mb={8}>
        <Flex align="center" justify="space-between" mb={4}>
          <Heading as="h2" size="md">Bills</Heading>
          <ButtonGroup isAttached bg="gray.100" borderRadius="xl" p={1}>
            <Button
              onClick={() => setBillsTab('revenue')}
              bg={billsTab === 'revenue' ? 'white' : 'transparent'}
              border="1px solid"
              borderColor={billsTab === 'revenue' ? 'gray.300' : 'transparent'}
              color={billsTab === 'revenue' ? 'blue.600' : 'gray.300'}
              fontSize="xs"
              fontWeight="medium"
            >
              Revenue
            </Button>
            <Button
              onClick={() => setBillsTab('refunds')}
              bg={billsTab === 'refunds' ? 'white' : 'transparent'}
              border="1px solid"
              borderColor={billsTab === 'refunds' ? 'gray.300' : 'transparent'}
              color={billsTab === 'refunds' ? 'blue.600' : 'gray.300'}
              fontSize="xs"
              fontWeight="medium"
            >
              Refunds
            </Button>
          </ButtonGroup>
        </Flex>

        <Box h="320px">
          <ResponsiveContainer width="100%" height="100%">
            {!hasBillsData() ? (
              <Flex direction="column" align="center" justify="center" h="full" color="gray.500" gap={2}>
                <Text fontWeight="medium">No data available</Text>
                <Text fontSize="sm">Please choose another filter</Text>
              </Flex>
            ) : billsTab === 'revenue' ? (
              <BarChart data={billsData.revenue.byPeriod} margin={CHART_MARGIN} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP}>
                <defs>
                  <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id="colorUnpaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.2} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="period" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE }} />
                <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE }} />
                <Tooltip />
                <Bar dataKey="paid" name="Paid" fill="url(#colorPaid)" radius={CHART_RADIUS} strokeWidth={STROKE_WIDTH} />
                <Bar dataKey="unpaid" name="Unpaid" fill="url(#colorUnpaid)" radius={CHART_RADIUS} strokeWidth={STROKE_WIDTH} />
              </BarChart>
            ) : (
              <BarChart data={billsData.refunds.byPeriod} margin={CHART_MARGIN} barSize={CHART_BAR_SIZE} barGap={CHART_BAR_GAP}>
                <defs>
                  <linearGradient id="colorIncomplete" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id="colorMissed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B7280" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#6B7280" stopOpacity={0.2} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="period" stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE }} />
                <YAxis stroke={AXIS_STROKE} tickLine={false} axisLine={false} tick={{ fill: AXIS_STROKE }} />
                <Tooltip />
                <Bar dataKey="incomplete" name="Incomplete" stackId="a" fill="url(#colorIncomplete)" radius={CHART_RADIUS} strokeWidth={STROKE_WIDTH} />
                <Bar dataKey="missed" name="Missed" stackId="a" fill="url(#colorMissed)" radius={CHART_RADIUS} strokeWidth={STROKE_WIDTH} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>

        <Grid templateColumns="repeat(3, 1fr)" gap={4} mt={4} fontSize="sm" color="gray.700">
          <Box>
            <Text fontSize="xs" color="gray.500">Revenue Total</Text>
            <Text mt={1} fontWeight="medium">{(billsData?.revenue?.total ?? 0).toLocaleString()}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Refunds Total</Text>
            <Text mt={1} fontWeight="medium">{(billsData?.refunds?.total ?? 0).toLocaleString()}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Refunds Incomplete</Text>
            <Text mt={1} fontWeight="medium">{(billsData?.refunds?.incomplete ?? 0).toLocaleString()}</Text>
          </Box>
        </Grid>
      </Card>
    </Box>
  );
};

export default ModernReports;